import { Fragment, useState, type FormEvent } from 'react';
import {
  GRANTABLE_ROLES,
  anonymiseAccount,
  createAccount,
  removeAccountIdentity,
  resetPassword,
  setAccountIdentity,
  setDisabled,
  setRole,
  type AccountView,
} from '../../api/admin';
import { MIN_PASSWORD_LENGTH } from '../../api/auth';
import { ca } from '../../i18n/ca';
import { Avatar } from '../IdentityPanel';
import { useConfirm, usePrompt } from '../useConfirm';
import { errorText, fmtWhen } from './shared';

interface Props {
  accounts: AccountView[];
  onChange: (account: AccountView) => void;
  onCreated: (account: AccountView) => void;
  onError: (text: string | null) => void;
  onNotice: (text: string | null) => void;
  /** The "new account" drawer, opened from the Shell's actions. */
  creating: boolean;
  onCloseCreate: () => void;
}

/** Accounts tab: a searchable table, a row that expands to the account's identities and actions, the create drawer. */
export function AccountsTab({
  accounts,
  onChange,
  onCreated,
  onError,
  onNotice,
  creating,
  onCloseCreate,
}: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const [confirm, confirmDialog] = useConfirm();
  const [prompt, promptDialog] = usePrompt();
  const q = query.trim().toLowerCase();
  const shown = q
    ? accounts.filter(
        (a) => a.handle.toLowerCase().includes(q) || a.displayName.toLowerCase().includes(q),
      )
    : accounts;

  async function run(action: () => Promise<AccountView>): Promise<boolean> {
    try {
      onChange(await action());
      onError(null);
      return true;
    } catch (err) {
      onError(errorText(err));
      return false;
    }
  }

  async function newPassword(account: AccountView) {
    const ask = ca.adminActions.resetPasswordPrompt(account.handle);
    const password = await prompt({
      title: ask.title,
      body: ask.body,
      confirmLabel: ca.adminActions.resetPassword,
      field: { label: ask.label, hint: ask.hint, minLength: MIN_PASSWORD_LENGTH },
    });
    if (!password) return;
    try {
      await resetPassword(account.id, password);
      onNotice(ca.adminActions.resetPasswordDone(account.handle));
      onError(null);
    } catch (err) {
      onError(errorText(err));
    }
  }

  async function anonymise(account: AccountView) {
    const ask = ca.adminActions.anonymiseConfirm(account.handle);
    if (!(await confirm({ ...ask, confirmLabel: ca.adminActions.anonymise, danger: true }))) return;
    if (await run(() => anonymiseAccount(account.id)))
      onNotice(ca.adminActions.anonymiseDone(account.handle));
  }

  return (
    <section className="stack" aria-label={ca.accounts}>
      {confirmDialog}
      {promptDialog}
      <p className="muted small">{ca.adminUi.intros.accounts}</p>
      <details className="identities card legend">
        <summary className="muted small">{ca.rolesLegend}</summary>
        <ul className="plain small">
          {GRANTABLE_ROLES.map((role) => (
            <li key={role}>
              <strong>{ca.roleNames[role]}</strong>: {ca.roleDescriptions[role]}
            </li>
          ))}
          <li>
            <strong>{ca.roleNames.PLAYER}</strong>: {ca.roleDescriptions.PLAYER}
          </li>
          <li>
            <strong>{ca.roleNames.COMMANDER}</strong>: {ca.roleDescriptions.COMMANDER}
          </li>
        </ul>
      </details>
      {creating && (
        <NewAccountForm
          onCreated={(a) => {
            onCreated(a);
            onCloseCreate();
          }}
          onError={onError}
          onCancel={onCloseCreate}
        />
      )}
      <div className="filters">
        <label>
          {ca.search}
          <input
            type="search"
            placeholder={ca.adminUi.search}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>
      {shown.length === 0 ? (
        <div className="empty">
          <i className="bi bi-people" aria-hidden="true" />
          <p>{ca.adminUi.noAccountsMatch}</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data accounts">
            <thead>
              <tr>
                <th>{ca.adminUi.colAccount}</th>
                <th>{ca.adminUi.colRoles}</th>
                <th>{ca.adminUi.colLastSeen}</th>
                <th>{ca.adminUi.colState}</th>
                <th>
                  <span className="visually-hidden">{ca.adminUi.colDetails}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((a) => {
                const expanded = open === a.id;
                const anonymised = a.handle.startsWith('deleted-');
                return (
                  <Fragment key={a.id}>
                    <tr aria-label={a.handle} className={expanded ? 'open' : ''}>
                      <td className="who-cell">
                        <span className="row who">
                          <Avatar url={a.avatarUrl} name={a.displayName || a.handle} size={32} />
                          <span>
                            <strong>{a.displayName}</strong>
                            <br />
                            <span className="muted small">@{a.handle}</span>
                          </span>
                        </span>
                      </td>
                      <td data-label={ca.adminUi.colRoles}>
                        <div className="row roles">
                          {GRANTABLE_ROLES.map((role) => (
                            <button
                              type="button"
                              key={role}
                              className={`chip toggle ${a.roles.includes(role) ? 'on' : ''}`}
                              aria-pressed={a.roles.includes(role)}
                              title={ca.roleDescriptions[role]}
                              disabled={anonymised}
                              onClick={() =>
                                void run(() => setRole(a.id, role, !a.roles.includes(role)))
                              }
                            >
                              {ca.roleNames[role]}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="muted small nowrap" data-label={ca.adminUi.colLastSeen}>
                        {a.lastSeenAt ? fmtWhen(a.lastSeenAt) : ca.never}
                      </td>
                      <td data-label={ca.adminUi.colState}>
                        {anonymised ? (
                          <span className="chip">{ca.adminUi.anonymised}</span>
                        ) : a.disabled ? (
                          <span className="chip bad">{ca.disabled}</span>
                        ) : (
                          <span className="chip ok">{ca.adminUi.active}</span>
                        )}
                      </td>
                      <td className="nowrap more-cell">
                        <button
                          type="button"
                          className="ghost small"
                          aria-expanded={expanded}
                          aria-label={
                            expanded ? ca.adminUi.collapse(a.handle) : ca.adminUi.expand(a.handle)
                          }
                          onClick={() => setOpen(expanded ? null : a.id)}
                        >
                          <i
                            className={`bi bi-chevron-${expanded ? 'up' : 'down'}`}
                            aria-hidden="true"
                          />
                        </button>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="details">
                        <td colSpan={5}>
                          <div className="stack">
                            <AccountIdentities account={a} onChange={onChange} onError={onError} />
                            <div className="row actions" aria-label={ca.adminUi.accountActions}>
                              <button
                                type="button"
                                className="small"
                                onClick={() => void newPassword(a)}
                              >
                                <i className="bi bi-key" aria-hidden="true" />{' '}
                                {ca.adminActions.resetPassword}
                              </button>
                              <button
                                type="button"
                                className="small"
                                onClick={() => void run(() => setDisabled(a.id, !a.disabled))}
                              >
                                <i
                                  className={`bi bi-${a.disabled ? 'play' : 'pause'}`}
                                  aria-hidden="true"
                                />{' '}
                                {a.disabled ? ca.enable : ca.disable}
                              </button>
                              <span className="grow" />
                              {!anonymised && (
                                <button
                                  type="button"
                                  className="small danger"
                                  onClick={() => void anonymise(a)}
                                >
                                  <i className="bi bi-person-x" aria-hidden="true" />{' '}
                                  {ca.adminActions.anonymise}
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** The create form, shown as a card above the table while the drawer is open. */
function NewAccountForm({
  onCreated,
  onError,
  onCancel,
}: {
  onCreated: (a: AccountView) => void;
  onError: (text: string | null) => void;
  onCancel: () => void;
}) {
  const [handle, setHandle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      onCreated(await createAccount({ handle, displayName, password }));
      onError(null);
    } catch (err) {
      onError(errorText(err));
    }
  }

  return (
    <form className="card form" onSubmit={submit} aria-label={ca.newAccount}>
      <h2>
        <i className="bi bi-person-plus" aria-hidden="true" /> {ca.newAccount}
      </h2>
      <p className="muted small">{ca.adminUi.newAccountIntro}</p>
      <div className="two">
        <label>
          {ca.handle}
          <input
            name="handle"
            required
            autoFocus
            autoCapitalize="none"
            pattern="[a-z0-9][a-z0-9._\-]{2,31}"
            value={handle}
            onChange={(e) => setHandle(e.target.value.toLowerCase())}
          />
          <span className="hint">{ca.handleHint}</span>
        </label>
        <label>
          {ca.displayName}
          <input
            name="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
      </div>
      <label>
        {ca.firstPassword}
        <input
          name="password"
          type="text"
          autoComplete="off"
          required
          minLength={10}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <span className="hint">{ca.firstPasswordHint}</span>
      </label>
      <div className="row actions">
        <button type="submit" className="primary">
          {ca.register}
        </button>
        <button type="button" className="ghost" onClick={onCancel}>
          {ca.adminUi.cancel}
        </button>
      </div>
    </form>
  );
}

/** An account's Steam id and Discord name as an admin sees and sets them: claims, until the person verifies. */
function AccountIdentities({
  account,
  onChange,
  onError,
}: {
  account: AccountView;
  onChange: (a: AccountView) => void;
  onError: (text: string | null) => void;
}) {
  const [steamId, setSteamId] = useState('');
  const [discordName, setDiscordName] = useState('');
  const details = account.identityDetails ?? [];

  async function claim(provider: 'STEAM' | 'DISCORD', subject: string) {
    if (!subject.trim()) return;
    try {
      onChange(await setAccountIdentity(account.id, provider, subject.trim()));
      if (provider === 'STEAM') setSteamId('');
      else setDiscordName('');
      onError(null);
    } catch (err) {
      onError(errorText(err));
    }
  }

  async function remove(provider: 'STEAM' | 'DISCORD') {
    try {
      onChange(await removeAccountIdentity(account.id, provider));
      onError(null);
    } catch (err) {
      onError(errorText(err));
    }
  }

  return (
    <section className="stack" aria-label={`${ca.adminIdentities} @${account.handle}`}>
      <h3>
        <i className="bi bi-link-45deg" aria-hidden="true" /> {ca.adminIdentities}
      </h3>
      <div className="row">
        <span className="chip ok">{ca.providers.PASSWORD}</span>
        {details.map((d) => (
          <span key={d.provider} className={`chip ${d.verified ? 'ok' : 'warn'}`}>
            {ca.providers[d.provider]} · {d.verified ? ca.identityVerified : ca.identityClaimed}
          </span>
        ))}
      </div>
      {details.length > 0 && (
        <ul className="list small">
          {details.map((d) => (
            <li key={d.provider}>
              <span className="grow">
                {ca.providers[d.provider]} <code>{d.subject}</code>
                {d.playerName && (
                  <span className="muted"> · {ca.identitySeenAs(d.playerName)}</span>
                )}
              </span>
              <button type="button" className="small" onClick={() => void remove(d.provider)}>
                {ca.claimRemove}
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="hint">{ca.adminIdentitiesHint}</p>
      <div className="two form">
        <label>
          {ca.steamIdLabel}
          <span className="row">
            <input
              aria-label={`${ca.steamIdLabel} @${account.handle}`}
              value={steamId}
              onChange={(e) => setSteamId(e.target.value)}
            />
            <button
              type="button"
              className="small"
              onClick={() => void claim('STEAM', steamId)}
              disabled={!steamId.trim()}
            >
              {ca.claimSave}
            </button>
          </span>
        </label>
        <label>
          {ca.discordNameLabel}
          <span className="row">
            <input
              aria-label={`${ca.discordNameLabel} @${account.handle}`}
              value={discordName}
              onChange={(e) => setDiscordName(e.target.value)}
            />
            <button
              type="button"
              className="small"
              onClick={() => void claim('DISCORD', discordName)}
              disabled={!discordName.trim()}
            >
              {ca.claimSave}
            </button>
          </span>
        </label>
      </div>
    </section>
  );
}
